import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material"
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../lib/axios";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import { desformatarTelefone } from "../../../../helpers/unmask";
import { formatarTelefone } from "../../../../helpers/masks";
import { useEffect, useMemo } from "react";
import { TConfigWhatsApp } from "..";

export default function DialogCriacaoEditIntegracaoWhatsapp({
    open,
    defaultValue,
    handleClose
}: DialogCriacaoEditIntegracaoWhatsappProps) {

    const queryClient = useQueryClient();

    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])

    const methods = useForm<TInternFilter>({
        defaultValues: {
            nome: defaultValue ? Object.keys(defaultValue)[0] : "",
            numero: defaultValue ? defaultValue[Object.keys(defaultValue)[0]].numero : "",
            token: defaultValue ? defaultValue[Object.keys(defaultValue)[0]].token : ""
        }
    });
    const { control, handleSubmit, reset } = methods

    //Querys e mutations
    const { data, isLoading: isLoadingConfiguracoes } = useQuery({
        queryKey: ["configuracoes-whatsapp"],
        queryFn: () => axiosClient.get("/configuracoes", {
            params: {
                chave: 'config_whatsapp'
            }
        })
    });

    const dataConfigWhatsapp: TConfigWhatsApp = useMemo(() => data?.data, [data])

    const { mutate: atualizarConfig, isLoading } = useMutation({
        mutationKey: 'atualizar-config-bancaria',
        mutationFn: (inputs: TFormEnvioConfigWhatsApp) => axiosClient.post(`atualizar-configuracoes`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao adicionar configuração!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["configuracoes-whatsapp"])
            handleClose()
            enqueueSnackbar('Atualização da configuração realizada com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: TInternFilter) => {

        const nomeChave = isEdit ? data.nome : data.nome.toLowerCase().replace(/ /g, "_");

        const params: TFormEnvioConfigWhatsApp = {
            chave: 'config_whatsapp',
            valor: {
                ...dataConfigWhatsapp,
                [nomeChave]: {
                    numero: desformatarTelefone(data.numero),
                    token: data.token
                }
            }
        }

        atualizarConfig(params)
    }

    useEffect(() => {
        if (defaultValue) {
            const nomeChave = Object.keys(defaultValue)[0];
            reset({
                nome: nomeChave,
                numero: defaultValue[nomeChave].numero,
                token: defaultValue[nomeChave].token
            });
        }
    }, [defaultValue, reset]);

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle >
                {`${!isEdit ? "Adicionar" : "Editar"} integração`}
                </DialogTitle>

                {
                    isLoadingConfiguracoes &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingConfiguracoes &&
                    <>
                        <DialogContent>
                            <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                                {
                                    !isEdit &&
                                    <Grid size={{xs: 12}}>
                                        <Controller
                                            name={'nome'}
                                            rules={{ required: 'Campo obrigatório' }}
                                            control={control}
                                            render={({ field, fieldState: { error, invalid } }) => (
                                                <TextField
                                                    {...field}
                                                    required
                                                    error={invalid}
                                                    fullWidth
                                                    label={'Nome'}
                                                    value={field.value}
                                                    onChange={(valor) => {
                                                        field.onChange(valor.target.value);
                                                    }}
                                                    helperText={invalid ? error?.message : ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                }
                                <Grid size={{xs: 6}}>
                                    <Controller
                                        name={'numero'}
                                        rules={{ required: 'Campo obrigatório' }}
                                        control={control}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <TextField
                                                {...field}
                                                required
                                                error={invalid}
                                                fullWidth
                                                defaultValue={defaultValue?.numero}
                                                label={'Número'}
                                                value={field.value}
                                                onChange={(valor) => {
                                                    const newValue = formatarTelefone(valor.target.value);
                                                    field.onChange(newValue);
                                                }}
                                                helperText={invalid ? error?.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{xs: 6}}>
                                    <Controller
                                        name={'token'}
                                        rules={{ required: 'Campo obrigatório' }}
                                        control={control}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <TextField
                                                {...field}
                                                required
                                                error={invalid}
                                                fullWidth
                                                label={'Token'}
                                                value={field.value}
                                                onChange={(valor) => {
                                                    field.onChange(valor.target.value);
                                                }}
                                                helperText={invalid ? error?.message : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>

                        </DialogContent>

                        <DialogActions>
                            <Button
                                onClick={handleClose}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                autoFocus
                                disabled={isLoading || isLoadingConfiguracoes}
                                onClick={handleSubmit(onSubmit)}
                            >
                                {
                                    (isLoading || isLoadingConfiguracoes) &&
                                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                }
                                 {`${!isEdit ? "Adicionar" : "Editar"}`}
                            </Button>
                        </DialogActions>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogCriacaoEditIntegracaoWhatsappProps {
    open: boolean
    handleClose: VoidFunction
    defaultValue: TConfigWhatsApp | null
}

type TInternFilter = {
    nome: string
    numero: string
    token: string
}


export type TFormEnvioConfigWhatsApp = {
    chave: string;
    valor: TConfigWhatsApp
}
